import { minutes, type DependencyType, type Minutes } from '@bigint-project/core';
import { ELAPSED_MINUTES_PER_DAY, MINUTES_PER_DAY, parseDurationInput } from './duration.js';

/**
 * 已解析的前置任務參照。
 * `ref` 保留使用者輸入的原始識別文字(WBS 碼,或退路的列號),
 * 由呼叫端(store)負責解析為實際 TaskId。
 */
export interface ParsedPredecessor {
  readonly ref: string;
  readonly type: DependencyType;
  readonly lagMinutes: Minutes;
  readonly lagIsElapsed: boolean;
}

const DEFAULT_TYPE: DependencyType = 'FS';
const VALID_TYPES: readonly DependencyType[] = ['FS', 'SS', 'FF', 'SF'];

// 整體格式:`<ref>[<FS|SS|FF|SF>][+|-<lag>]`,例如 "3"、"3FS"、"3FS+2d"、"5SS-1d"。
const SPLIT_PATTERN = /^([^+-]+)([+-].+)?$/;

/**
 * 解析單一前置任務片段,例如 "3"、"3FS"、"3FS+2d"、"5SS-1d"。
 * 非法輸入回傳 null。
 */
function parseSingle(raw: string): ParsedPredecessor | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const splitMatch = SPLIT_PATTERN.exec(trimmed);
  if (!splitMatch) return null;
  const [, refAndType, lagPart] = splitMatch;
  if (refAndType === undefined) return null;

  // ref 僅接受數字與點(WBS 碼或列號,例如 "3"、"1.2"),其後可接型別代碼。
  // 其餘字元(未知型別代碼)一律視為非法輸入。
  const typeMatch = /^(\d+(?:\.\d+)*)\s*(FS|SS|FF|SF)?$/i.exec(refAndType.trim());
  if (!typeMatch) return null;
  const [, refRaw, typeRaw] = typeMatch;
  const ref = (refRaw ?? '').trim();
  if (ref === '') return null;

  const type = typeRaw ? (typeRaw.toUpperCase() as DependencyType) : DEFAULT_TYPE;
  if (!VALID_TYPES.includes(type)) return null;

  let lagMinutes: Minutes = minutes(0);
  let lagIsElapsed = false;

  if (lagPart) {
    const sign = lagPart.startsWith('-') ? -1 : 1;
    const durationText = lagPart.slice(1);
    if (durationText.trim() === '') return null;
    const parsedLag = parseDurationInput(durationText);
    if (parsedLag === null) return null;
    lagIsElapsed = /^\s*\d+(?:\.\d+)?\s*e/i.test(durationText);
    lagMinutes = minutes(sign * (parsedLag as unknown as number));
  }

  return { ref, type, lagMinutes, lagIsElapsed };
}

/**
 * 解析前置任務欄位輸入,支援逗號分隔多筆,例如 "3FS+2d,5SS-1d"。
 * 任一片段非法時回傳 null(整體視為非法輸入,不做部分成功)。
 */
export function parsePredecessorInput(input: string): readonly ParsedPredecessor[] | null {
  const trimmed = input.trim();
  if (trimmed === '') return [];

  const parts = trimmed.split(',');
  const result: ParsedPredecessor[] = [];
  for (const part of parts) {
    const parsed = parseSingle(part);
    if (parsed === null) return null;
    result.push(parsed);
  }
  return result;
}

/**
 * 把 lag 格式化為顯示片段,例如 `+2d`、`-1d`、`+3ed`;無 lag 時為空字串。
 *
 * 彈性(elapsed)lag 以連續 24 小時計(1440 分鐘/天),一般 lag 以工作天計
 * (480 分鐘/天)——兩者換算基準不同,不可共用同一除數。
 */
export function formatLag(lagMinutes: Minutes, lagIsElapsed: boolean): string {
  const lag = lagMinutes as unknown as number;
  if (lag === 0) return '';
  const sign = lag > 0 ? '+' : '-';
  const divisor = lagIsElapsed ? ELAPSED_MINUTES_PER_DAY : MINUTES_PER_DAY;
  const unitSuffix = lagIsElapsed ? 'ed' : 'd';
  return `${sign}${Math.abs(lag) / divisor}${unitSuffix}`;
}

/**
 * 將解析結果格式化回顯示字串,例如 "3,5SS-1d"。
 *
 * **FS(前置完成才能開始)是預設關係,顯示時一律省略型別代碼** —— 絕大多數依賴都是
 * FS,把 `FS` 印出來只會讓不熟 MS Project 術語的人看到看不懂的英文縮寫。SS/FF/SF
 * 不是預設值,必須標示,否則語意會遺失。
 *
 * 省略後仍可被 `parsePredecessorInput` 原樣讀回(沒有型別代碼即視為 FS),
 * 因此「顯示 → 再次編輯 → 落地」的往返結果不變。
 */
export function formatPredecessorList(items: readonly ParsedPredecessor[]): string {
  return items
    .map((item) => {
      const typeText = item.type === DEFAULT_TYPE ? '' : item.type;
      return `${item.ref}${typeText}${formatLag(item.lagMinutes, item.lagIsElapsed)}`;
    })
    .join(',');
}
